import { useEffect, useRef, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import { getErrorMessage } from "metabase/api/utils";
import { useDispatch } from "metabase/lib/redux";
import { checkNotNull } from "metabase/lib/types";
import * as Lib from "metabase-lib";
import type Question from "metabase-lib/v1/Question";
import type NativeQuery from "metabase-lib/v1/queries/NativeQuery";
import type { DatabaseId } from "metabase-types/api";

import { prepareDatabaseSwitch } from "./prepare-database-switch";

export function useDatabaseSwitch(
  question: Question,
  onChange: (query: NativeQuery) => Promise<unknown>,
) {
  const dispatch = useDispatch();
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRequest = useRef<symbol>();
  const latestQuestion = useRef(question);
  latestQuestion.current = question;

  useEffect(
    () => () => {
      pendingRequest.current = undefined;
    },
    [],
  );

  const cancelSwitch = () => {
    pendingRequest.current = undefined;
    setIsSwitching(false);
    setError(null);
  };

  const setDatabaseId = async (databaseId: DatabaseId) => {
    if (question.databaseId() === databaseId || pendingRequest.current) {
      return;
    }
    const request = Symbol();
    pendingRequest.current = request;
    setError(null);
    try {
      const hasFieldBindings =
        Lib.nativeQueryFieldIds(question.query()).length > 0;
      setIsSwitching(hasFieldBindings);
      const nextQuery = hasFieldBindings
        ? await dispatch(prepareDatabaseSwitch(question, databaseId))
        : checkNotNull(question.legacyNativeQuery())
            .setDatabaseId(databaseId)
            .setDefaultCollection();
      if (pendingRequest.current !== request) {
        return;
      }
      if (!_.isEqual(latestQuestion.current.card(), question.card())) {
        throw new Error(
          t`The question changed while its metadata was loading. Please try switching databases again.`,
        );
      }
      await onChange(nextQuery);
    } catch (error) {
      if (pendingRequest.current === request) {
        setError(getErrorMessage(error));
      }
    } finally {
      if (pendingRequest.current === request) {
        pendingRequest.current = undefined;
        setIsSwitching(false);
      }
    }
  };

  return { setDatabaseId, isSwitching, error, cancelSwitch };
}
